import type { LastBotCore } from "../core/LastBotCore";
import { getSlotDescriptor, type SlotId } from "./slotRegistry";

/**
 * Dispatch the CustomEvent associated with an interactive slot, on the
 * `<lastbot-chat>` host element if one exists.
 *
 * Returns `true` if the default action should run (no listener called
 * `preventDefault`) or if there's no host element to dispatch on (headless
 * mode). Returns `false` if a listener cancelled the event.
 *
 * Usage from an interactive slot's onClick:
 *
 *     onClick={() => {
 *       if (dispatchSlotEvent(core, "login-button")) {
 *         core.showPage("login");
 *       }
 *     }}
 *
 * Or via `runSlotAction` for the common case where the default action is
 * just a core method call with no payload.
 */
export const dispatchSlotEvent = <T>(
  core: LastBotCore,
  slotId: SlotId,
  detail?: T,
): boolean => {
  const descriptor = getSlotDescriptor(slotId);
  if (!descriptor) {
    console.warn(
      `[lastbot-chat] dispatchSlotEvent: unknown slot id "${slotId}"`,
    );
    return true;
  }
  if (descriptor.kind !== "interactive" || !descriptor.event) {
    console.warn(
      `[lastbot-chat] dispatchSlotEvent: slot "${slotId}" is not interactive`,
    );
    return true;
  }
  const host = core.getHostElement();
  if (!host) {
    // Headless / pre-mount — no DOM event surface. Caller should fall back
    // to the default action.
    return true;
  }
  // Always expose widgetId/placementId on the detail so a host page with multiple
  // placements can tell which placement fired the slot event, alongside any
  // caller-supplied payload. Spreading `undefined` yields `{}`.
  const event = new CustomEvent(descriptor.event, {
    bubbles: true,
    composed: true,
    cancelable: true,
    detail: {
      ...(detail as Record<string, unknown> | undefined),
      widgetId: core.config.widgetId,
      placementId: core.config.placementId,
    },
  });
  // dispatchEvent returns false if any handler called preventDefault.
  return host.dispatchEvent(event);
};

/**
 * Default action lookup. Maps an interactive slot id to the core method that
 * runs when no listener cancels the dispatched event. Slot ids whose default
 * action needs a payload (composer-text, conversation-starter) are NOT in
 * this map — those have to be handled inline at the call site because the
 * payload comes from the React event handler, not from the slot.
 *
 * Returning `null` means "no default" — runSlotAction is a no-op for that
 * slot. The dispatched event still fires, embedders can still catch it.
 */
const defaultActionFor = (
  slotId: SlotId,
  core: LastBotCore,
): (() => void) | null => {
  switch (slotId) {
    case "toggle-button":
      // Fire-and-forget: slot actions are void; open()'s panel-ready promise
      // (LAS-2166) pre-handles its own rejection.
      return () => {
        void core.open();
      };
    case "close-button":
      return () => core.close();
    case "fullscreen-button":
      return () => core.toggleFullscreen();
    case "login-button":
      return () => core.showPage("login");
    case "history-button":
      return () => core.showPage("history");
    default:
      return null;
  }
};

/**
 * Common case helper: dispatch the slot's event and run the matching core
 * method if no listener prevented it. Returns nothing — the dispatcher
 * already handled both branches.
 *
 * Slots whose default action needs a payload (e.g. `composer-text` needs
 * the message text) should call `dispatchSlotEvent` directly and act on the
 * boolean return.
 */
export const runSlotAction = (core: LastBotCore, slotId: SlotId): void => {
  const shouldRunDefault = dispatchSlotEvent(core, slotId);
  if (!shouldRunDefault) return;
  const action = defaultActionFor(slotId, core);
  if (action) action();
};

/**
 * Dispatch an embedder-defined CustomEvent triggered by a `data-custom-event`
 * attribute on an element inside an embedder template. Mirrors the slot-event
 * dispatcher above (bubbling + composed + cancelable on the host element) but
 * has no registry lookup, no default action, and no opinion about the event
 * name — that's all embedder-controlled.
 *
 * The `detail` carries:
 *   - `payload`: optional string value from `data-custom-event-payload`.
 *   - `dataset`: all *other* `data-*` attributes on the element (excluding
 *     `data-custom-event` / `data-custom-event-payload`), keyed in camelCase
 *     to mirror `HTMLElement.dataset`. Lets embedders parametrise their
 *     custom buttons without packing everything into the payload string.
 *   - `widgetId` / `placementId`: the firing placement's identity, so a page
 *     with multiple placements can attribute the event.
 *
 * Returns `true` when no listener cancelled the event, `false` otherwise.
 * Headless mode (no host element) returns `true` — there's no DOM to
 * dispatch on, so this is a no-op.
 */
export const dispatchCustomTemplateEvent = (
  core: LastBotCore,
  eventName: string,
  detail: { payload?: string; dataset: Record<string, string> },
): boolean => {
  const host = core.getHostElement();
  if (!host) return true;
  return host.dispatchEvent(
    new CustomEvent(eventName, {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: {
        ...detail,
        widgetId: core.config.widgetId,
        placementId: core.config.placementId,
      },
    }),
  );
};
